import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import OptOutCard from "../components/OptOutCard";
import { resolveShortLink, resolveTrackedLink } from "../api/optOutApi";

/**
 * Public landing page for the short links inside a text
 * (crm.collings.com.au/l/<code>).
 *
 * Every character in an SMS is billed, so since 28 Aug the backend replaces the
 * long links in a text - a ~290-character signed preferences link, or any of our
 * own links plus its tracking - with a seven-character code (CG: "Can we please
 * reduce the characters used by using a link shortener like bitly"). That code
 * has to resolve somewhere, and it points at this domain to stay short and
 * branded. This domain is the React app, so the page does the lookup and sends
 * the browser on - exactly the arrangement the /s/ opt-out link already uses.
 *
 * Without a route here the link would open a blank single-page app, which is the
 * bug the tap-to-stop link hit before it got its own page.
 *
 * Serves /k/<code> too (`tracked`), which is the same redirect with the
 * recipient's name on it - minted per person so the analytics can say WHICH
 * buyer tapped "Join the Collings platform" (CG, Monday 13068863308). Same page
 * because it is the same job and the same failure to avoid; the only difference
 * is that a /k/ link arrives from an email carrying that campaign's UTM tags, so
 * the query string is handed on rather than dropped.
 */
const ShortLinkPage = ({ tracked = false }) => {
  const { code } = useParams();
  const { search } = useLocation();
  const [failed, setFailed] = useState(false);
  const requested = useRef(false);

  useEffect(() => {
    // The effect runs twice under React StrictMode in development; one lookup
    // is enough, and the second would double-count the click.
    if (requested.current) return;
    requested.current = true;

    let active = true;
    const lookup = tracked ? resolveTrackedLink(code, search) : resolveShortLink(code);
    lookup
      .then((data) => {
        if (!active) return;
        if (data?.url) {
          // replace, not assign: Back should return to whatever they were doing,
          // not bounce them through the redirect again.
          window.location.replace(data.url);
          return;
        }
        setFailed(true);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
    };
  }, [code, search, tracked]);

  return (
    <OptOutCard
      state={failed ? "error" : "loading"}
      title={failed ? "Link not recognised" : "One moment"}
      message={
        failed
          ? "This link has expired or is not one of ours. Reply to any email from us and we'll send you a fresh one."
          : "Taking you there now..."
      }
    />
  );
};

export default ShortLinkPage;
